import { capitalizeEveryWord } from './chunk-YDGMD7UV.mjs';
import { offeringHidesSecurityLabel, offeringIsTypeRegA, offeringIsRegS } from './chunk-UPSZ2GCU.mjs';
import { PAYMENT_TYPES } from './chunk-BFDKW44N.mjs';
import { INDIVIDUAL, INTERNATIONAL, ENTITY } from './chunk-4VKGU7BH.mjs';
import { NumberFormatter } from '@equidefi/i18n';

var isRegSEligible = ({
  isOfferingRegS,
  entityType,
  citizenship,
  isResidentInsideUS,
  isRegisteredInUS
}) => {
  if (!isOfferingRegS) return true;
  if (entityType === INDIVIDUAL) {
    return citizenship === INTERNATIONAL && isResidentInsideUS === false;
  }
  if (entityType === ENTITY) {
    return isRegisteredInUS === false;
  }
  return false;
};
var investments_default = isRegSEligible;
function calculateInvestmentFees(investment) {
  const { amount, payments, offering } = investment;
  const successfulPayments = payments.filter((payment) => payment.status === "succeeded");
  const appFeeTotal = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.fee || 0);
  }, 0);
  const totalFeeSum = successfulPayments.reduce((sum, payment) => {
    return sum + Number(payment.total_fee || 0);
  }, 0);
  const stripeFeeTotal = totalFeeSum - appFeeTotal;
  const grossAmount = Number(amount || 0);
  const netAmount = grossAmount - totalFeeSum;
  const paymentType = successfulPayments[0]?.payment_type || PAYMENT_TYPES.CARD;
  const feeRate = paymentType === PAYMENT_TYPES.US_BANK_ACCOUNT ? Number(offering?.ach_fee || 0) * 100 : Number(offering?.credit_card_fee || 0) * 100;
  return {
    app_fee: appFeeTotal,
    app_fee_rate: feeRate,
    stripe_fee: stripeFeeTotal,
    net_amount: netAmount
  };
}
function investmentBcode(value) {
  if (!value) return null;
  const trimmedBcode = String(value || "").trim();
  return trimmedBcode || null;
}
function formatInvestmentFeeDisplay(fees) {
  return `$${fees.app_fee.toFixed(2)} (${fees.app_fee_rate.toFixed(2)}%)`;
}
var resolvePaymentTypeFromData = (data) => {
  const paymentMethod = data?.last_payment_error?.payment_method ?? data?.payment_method ?? data?.latest_charge?.payment_method_details;
  return paymentMethod?.type;
};
var paymentTypeDisplay = (payment) => {
  const paymentType = payment.payment_type || resolvePaymentTypeFromData(payment.data);
  if (paymentType === PAYMENT_TYPES.US_BANK_ACCOUNT) return "US Bank Account";
  if (paymentType === PAYMENT_TYPES.STRIPE_WIRE) return "Wire Transfer";
  if (paymentType) return capitalizeEveryWord(paymentType);
  return "Manual";
};
var investmentIsCompleted = (investment) => {
  if (!investment) return false;
  return investment?.status !== "PENDING";
};
var investmentDefaultEventHistoryTitle = (event, status) => {
  return [event === "KYC" ? "KYC" : capitalizeEveryWord(event), capitalizeEveryWord(status)].join(" ");
};
var calculateMaxSubscriptionAmount = ({
  annualIncome,
  netWorth,
  investments = []
}) => {
  const income = Number.isNaN(Number(annualIncome)) ? 0 : Number(annualIncome);
  const worth = Number.isNaN(Number(netWorth)) ? 0 : Number(netWorth);
  const highest = Math.max(income, worth) ?? 0;
  const investedAmount = investments.reduce((acc, currentInv) => {
    if (currentInv.status !== "CANCELED") {
      return currentInv.amount + acc;
    }
    return acc;
  }, 0);
  const maxAmount = highest / 10 - investedAmount;
  return Math.max(maxAmount, 0);
};
var formatSecurities = ({
  quantity,
  offering,
  capitalize = false
}) => {
  if (offeringHidesSecurityLabel(offering)) {
    const genericLabel = capitalize ? "Securities" : "securities";
    return {
      quantity: void 0,
      label: genericLabel,
      toString() {
        return genericLabel;
      }
    };
  }
  const formattedQuantity = quantity && NumberFormatter.format(quantity);
  const formatLabel = (singular, plural) => {
    const shouldUsePlural = !quantity || quantity > 1;
    let result = (shouldUsePlural ? plural : singular).toLowerCase();
    if (capitalize) result = capitalizeEveryWord(result);
    return result;
  };
  const label = formatLabel(offering?.security_type_label ?? "Share", offering?.security_type_plural ?? "Shares");
  return {
    quantity: formattedQuantity,
    label,
    toString() {
      if (!formattedQuantity) return label;
      return `${formattedQuantity} ${label}`;
    }
  };
};
var getInvestmentStepLabel = (investment, offering = {}) => {
  if (investment.status === "CANCELED") return "CANCELED";
  const collectsAdditionalInfo = offeringIsTypeRegA(offering) || offeringIsRegS(offering);
  let step = investment.next ?? "N/A";
  if (investment.next === "DILIGENCE") {
    step = "REVIEW";
  }
  if (collectsAdditionalInfo && step === "ACCREDITATION") {
    step = "ADDITIONAL INFO";
  }
  return step;
};
var checkKycCompleted = (investment) => {
  if (!investment.signing_parties || investment.signing_parties.length < 2 || !investment.inquiries || !investment.vault) {
    return false;
  }
  let requiredSigners = investment.signing_parties.filter((sp) => !sp.is_countersigner).length;
  const approvedCount = investment.inquiries.filter((i) => i.status === "approved").length;
  if (investment.vault.entity_type === "ENTITY") {
    if (investment.inquiries.length > 0 && approvedCount === investment.inquiries.length) {
      return true;
    }
    requiredSigners += 1;
  }
  return approvedCount >= requiredSigners;
};
var checkAgreementCompleted = (investment) => {
  if (!investment.investment_agreements || investment.investment_agreements.length === 0) {
    return false;
  }
  return investment.investment_agreements.every(
    (agreement) => agreement.status === "SIGNED" || agreement.status === "COMPLETED"
  );
};
var getInvestmentUrl = (investment, baseUrl) => {
  if (!investment.offering_id || !investment.id) {
    return "";
  }
  const path = `/offerings/${investment.offering_id}/investments/${investment.id}`;
  if (!baseUrl || baseUrl.trim() === "") {
    return path;
  }
  try {
    const cleanBaseUrl = baseUrl.replace(/\/$/, "");
    const url = new URL(path, cleanBaseUrl);
    return url.toString();
  } catch (error) {
    return path;
  }
};

export { calculateInvestmentFees, calculateMaxSubscriptionAmount, checkAgreementCompleted, checkKycCompleted, formatInvestmentFeeDisplay, formatSecurities, getInvestmentStepLabel, getInvestmentUrl, investmentBcode, investmentDefaultEventHistoryTitle, investmentIsCompleted, investments_default, paymentTypeDisplay };
